/**
 * Edit Bid Modal
 *
 * Form for editing an existing bid's pricing and description.
 * PURELY PRESENTATIONAL - validation happens on backend.
 *
 * DDD Compliance:
 * - Uses mutation hook for bid updates
 * - Only UI validation (required fields, basic formats)
 * - Backend handles all business logic validation
 * - Displays optimistic updates
 */

import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { useUpdateBid } from '@/application/service-provider/hooks/useBidMutations';
import type { BidDetailViewModel } from '@/application/service-provider/view-models/BidViewModel';
import type { CreateBidRequest } from '@calwmp/shared';

interface EditBidModalProps {
  bid: BidDetailViewModel;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditBidModal({ bid, onClose, onSuccess }: EditBidModalProps) {
  const updateMutation = useUpdateBid();

  // Form state
  const [formData, setFormData] = useState({
    haulingRate: bid.fullPricing.haulingRate?.amount.toString() || '',
    container10yd:
      bid.fullPricing.containerRentals.find((r) => r.size === '10yd')?.unitPrice.toString() ||
      '',
    container20yd:
      bid.fullPricing.containerRentals.find((r) => r.size === '20yd')?.unitPrice.toString() ||
      '',
    container30yd:
      bid.fullPricing.containerRentals.find((r) => r.size === '30yd')?.unitPrice.toString() ||
      '',
    container40yd:
      bid.fullPricing.containerRentals.find((r) => r.size === '40yd')?.unitPrice.toString() ||
      '',
    serviceDescription: bid.serviceDescription || '',
    message: bid.message || '',
  });

  const [errors, setErrors] = useState<Record<string, string>>({});

  // Basic UI validation
  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    // At least one pricing field required
    if (
      !formData.haulingRate &&
      !formData.container10yd &&
      !formData.container20yd &&
      !formData.container30yd &&
      !formData.container40yd
    ) {
      newErrors.general = 'At least one pricing field is required';
    }

    // Validate pricing values if provided
    const validatePrice = (value: string, field: string) => {
      if (value) {
        const num = parseFloat(value);
        if (isNaN(num) || num <= 0) {
          newErrors[field] = 'Must be a positive number';
        }
      }
    };

    validatePrice(formData.haulingRate, 'haulingRate');
    validatePrice(formData.container10yd, 'container10yd');
    validatePrice(formData.container20yd, 'container20yd');
    validatePrice(formData.container30yd, 'container30yd');
    validatePrice(formData.container40yd, 'container40yd');

    // Service description required
    if (!formData.serviceDescription.trim()) {
      newErrors.serviceDescription = 'Service description is required';
    }

    // Message length check
    if (formData.message && formData.message.length > 500) {
      newErrors.message = 'Message must be 500 characters or less';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }

    // Build proposed pricing object
    const proposedPricing: Partial<CreateBidRequest['proposed_pricing']> = {};

    if (formData.haulingRate) {
      proposedPricing.hauling_rate_per_ton = parseFloat(formData.haulingRate);
    }
    if (formData.container10yd) {
      proposedPricing.bin_rental_10yd = parseFloat(formData.container10yd);
    }
    if (formData.container20yd) {
      proposedPricing.bin_rental_20yd = parseFloat(formData.container20yd);
    }
    if (formData.container30yd) {
      proposedPricing.bin_rental_30yd = parseFloat(formData.container30yd);
    }
    if (formData.container40yd) {
      proposedPricing.bin_rental_40yd = parseFloat(formData.container40yd);
    }

    try {
      await updateMutation.mutateAsync({
        bidId: bid.id,
        updates: {
          proposed_pricing: proposedPricing,
          service_description: formData.serviceDescription,
          message: formData.message || undefined,
        },
      });

      onSuccess();
    } catch (error) {
      // Error will be shown via mutation error state
      console.error('Failed to update bid:', error);
    }
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field
    if (errors[field]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Edit Bid</DialogTitle>
          <DialogDescription>
            Update your pricing and service details for this bid
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-6 mt-4">
          {/* General Error */}
          {errors.general && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">{errors.general}</p>
            </div>
          )}

          {/* Mutation Error */}
          {updateMutation.isError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-sm text-red-800">
                Failed to update bid. Please try again.
              </p>
            </div>
          )}

          {/* Pricing Section */}
          <div>
            <h3 className="text-lg font-semibold text-neutral-900 mb-3">Pricing</h3>

            {/* Hauling Rate */}
            <div className="mb-4">
              <label htmlFor="haulingRate" className="block text-sm font-medium text-neutral-700 mb-1">
                Hauling Rate ($/ton)
              </label>
              <input
                type="number"
                id="haulingRate"
                step="0.01"
                min="0"
                value={formData.haulingRate}
                onChange={(e) => handleChange('haulingRate', e.target.value)}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                  errors.haulingRate
                    ? 'border-red-300 focus:ring-red-500'
                    : 'border-neutral-300 focus:ring-primary-500'
                }`}
                placeholder="75.00"
              />
              {errors.haulingRate && (
                <p className="text-sm text-red-600 mt-1">{errors.haulingRate}</p>
              )}
            </div>

            {/* Container Rates */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="container10yd" className="block text-sm font-medium text-neutral-700 mb-1">
                  10 Yard Container ($)
                </label>
                <input
                  type="number"
                  id="container10yd"
                  step="0.01"
                  min="0"
                  value={formData.container10yd}
                  onChange={(e) => handleChange('container10yd', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.container10yd
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-neutral-300 focus:ring-primary-500'
                  }`}
                  placeholder="250.00"
                />
                {errors.container10yd && (
                  <p className="text-sm text-red-600 mt-1">{errors.container10yd}</p>
                )}
              </div>

              <div>
                <label htmlFor="container20yd" className="block text-sm font-medium text-neutral-700 mb-1">
                  20 Yard Container ($)
                </label>
                <input
                  type="number"
                  id="container20yd"
                  step="0.01"
                  min="0"
                  value={formData.container20yd}
                  onChange={(e) => handleChange('container20yd', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.container20yd
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-neutral-300 focus:ring-primary-500'
                  }`}
                  placeholder="350.00"
                />
                {errors.container20yd && (
                  <p className="text-sm text-red-600 mt-1">{errors.container20yd}</p>
                )}
              </div>

              <div>
                <label htmlFor="container30yd" className="block text-sm font-medium text-neutral-700 mb-1">
                  30 Yard Container ($)
                </label>
                <input
                  type="number"
                  id="container30yd"
                  step="0.01"
                  min="0"
                  value={formData.container30yd}
                  onChange={(e) => handleChange('container30yd', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.container30yd
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-neutral-300 focus:ring-primary-500'
                  }`}
                  placeholder="450.00"
                />
                {errors.container30yd && (
                  <p className="text-sm text-red-600 mt-1">{errors.container30yd}</p>
                )}
              </div>

              <div>
                <label htmlFor="container40yd" className="block text-sm font-medium text-neutral-700 mb-1">
                  40 Yard Container ($)
                </label>
                <input
                  type="number"
                  id="container40yd"
                  step="0.01"
                  min="0"
                  value={formData.container40yd}
                  onChange={(e) => handleChange('container40yd', e.target.value)}
                  className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                    errors.container40yd
                      ? 'border-red-300 focus:ring-red-500'
                      : 'border-neutral-300 focus:ring-primary-500'
                  }`}
                  placeholder="550.00"
                />
                {errors.container40yd && (
                  <p className="text-sm text-red-600 mt-1">{errors.container40yd}</p>
                )}
              </div>
            </div>
          </div>

          {/* Service Description */}
          <div>
            <label htmlFor="serviceDescription" className="block text-sm font-medium text-neutral-700 mb-1">
              Service Description <span className="text-red-500">*</span>
            </label>
            <textarea
              id="serviceDescription"
              rows={4}
              value={formData.serviceDescription}
              onChange={(e) => handleChange('serviceDescription', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.serviceDescription
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-neutral-300 focus:ring-primary-500'
              }`}
              placeholder="Describe the services you'll provide..."
            />
            {errors.serviceDescription && (
              <p className="text-sm text-red-600 mt-1">{errors.serviceDescription}</p>
            )}
          </div>

          {/* Message to Applicant */}
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-neutral-700 mb-1">
              Message to Applicant (Optional)
            </label>
            <textarea
              id="message"
              rows={3}
              value={formData.message}
              onChange={(e) => handleChange('message', e.target.value)}
              className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 ${
                errors.message
                  ? 'border-red-300 focus:ring-red-500'
                  : 'border-neutral-300 focus:ring-primary-500'
              }`}
              placeholder="Any additional information for the applicant..."
              maxLength={500}
            />
            <div className="flex justify-between mt-1">
              {errors.message ? (
                <p className="text-sm text-red-600">{errors.message}</p>
              ) : (
                <p className="text-xs text-neutral-500">Max 500 characters</p>
              )}
              <p className="text-xs text-neutral-500">
                {formData.message.length}/500
              </p>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={updateMutation.isPending}
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-primary"
              disabled={updateMutation.isPending}
            >
              {updateMutation.isPending ? 'Updating...' : 'Update Bid'}
            </button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
